import { useEffect, useState, useRef } from "react";
import { Bell, Check, MailCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

/**
 * NotificationPanel — bell icon with unread badge + popover list.
 * Props:
 *   items: [{ id, subject, body, sent_at, channel, read? }]
 *   onMarkAllRead?: () => void
 *   onItemClick?: (item) => void
 */
export default function NotificationPanel({ items = [], onMarkAllRead, onItemClick, className = "", ...rest }) {
  const unreadCount = items.filter((i) => !i.read).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`relative h-10 w-10 rounded-full flex items-center justify-center text-slate-600 hover:text-[#0A192F] hover:bg-slate-100 transition-colors ${className}`}
          data-testid={rest["data-testid"] || "notification-bell"}
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] rounded-full bg-[#D4AF37] text-[#0A192F] text-[10px] font-bold px-1 flex items-center justify-center border-2 border-white">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="end" data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-[#0A192F] to-[#1E3A8A] text-white rounded-t-md">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
              Inbox
            </p>
            <p className="text-sm font-semibold mt-0.5">
              {items.length} update{items.length === 1 ? "" : "s"}
            </p>
          </div>
          {unreadCount > 0 && onMarkAllRead && (
            <button
              onClick={onMarkAllRead}
              className="text-xs text-[#D4AF37] hover:text-white flex items-center gap-1"
              data-testid="mark-all-read-button"
            >
              <Check className="h-3 w-3" /> Mark read
            </button>
          )}
        </div>
        <div className="max-h-[400px] overflow-auto divide-y divide-slate-100">
          {items.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <MailCheck className="h-5 w-5 text-slate-300 mx-auto" />
              <p className="mt-2 text-xs text-slate-500">You&apos;re all caught up.</p>
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => onItemClick && onItemClick(n)}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${n.read ? "" : "bg-[#D4AF37]/5"}`}
                data-testid={`notification-item-${n.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-xs font-semibold text-[#0A192F] line-clamp-1">{n.subject}</p>
                  {n.channel && (
                    <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#1E3A8A] shrink-0">
                      {n.channel}
                    </span>
                  )}
                </div>
                {n.body && (
                  <p className="mt-1 text-xs text-slate-500 line-clamp-2 leading-relaxed">
                    {n.body}
                  </p>
                )}
                {n.sent_at && (
                  <p className="mt-1 text-[10px] text-slate-400">
                    {new Date(n.sent_at).toLocaleString()}
                  </p>
                )}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
